import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import type { InviteDto, ManageProject, ManageTeam, Task, UsersResponse } from '../../types/api';

function invalidateAll(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: qk.teams });
  void qc.invalidateQueries({ queryKey: qk.projects });
  void qc.invalidateQueries({ queryKey: qk.users });
  void qc.invalidateQueries({ queryKey: qk.invites });
  void qc.invalidateQueries({ queryKey: qk.bootstrap });
}

export const useTeams = () =>
  useQuery({ queryKey: qk.teams, queryFn: () => apiGet<{ teams: ManageTeam[] }>('/api/teams') });

export const useProjects = () =>
  useQuery({ queryKey: qk.projects, queryFn: () => apiGet<{ projects: ManageProject[] }>('/api/projects') });

export const useProjectTasks = (projectId: string, enabled: boolean) =>
  useQuery({
    queryKey: qk.projectTasks(projectId),
    queryFn: () => apiGet<{ tasks: Task[] }>(`/api/projects/${projectId}/tasks`),
    enabled,
  });

export const useUsers = () => useQuery({ queryKey: qk.users, queryFn: () => apiGet<UsersResponse>('/api/users') });

export const useInvites = () =>
  useQuery({ queryKey: qk.invites, queryFn: () => apiGet<{ invites: InviteDto[] }>('/api/invites') });

export function useManageMutations() {
  const qc = useQueryClient();
  const done = () => invalidateAll(qc);
  const invalidateTasks = (projectId: string) => void qc.invalidateQueries({ queryKey: qk.projectTasks(projectId) });

  return {
    createTeam: useMutation({ mutationFn: (name: string) => apiPost('/api/teams', { name }), onSuccess: done }),
    renameTeam: useMutation({ mutationFn: (v: { id: string; name: string }) => apiPatch(`/api/teams/${v.id}`, { name: v.name }), onSuccess: done }),
    deleteTeam: useMutation({ mutationFn: (id: string) => apiDelete(`/api/teams/${id}`), onSuccess: done }),
    toggleMember: useMutation({
      mutationFn: (v: { teamId: string; userId: string }) => apiPost(`/api/teams/${v.teamId}/members/${v.userId}/toggle`),
      onSuccess: done,
    }),
    createProject: useMutation({ mutationFn: (v: { name: string; teamIds: string[] }) => apiPost('/api/projects', v), onSuccess: done }),
    updateProject: useMutation({ mutationFn: (v: { id: string; name: string; teamIds: string[] }) => apiPatch(`/api/projects/${v.id}`, { name: v.name, teamIds: v.teamIds }), onSuccess: done }),
    archiveProject: useMutation({ mutationFn: (id: string) => apiPost(`/api/projects/${id}/archive`), onSuccess: done }),
    createTask: useMutation({
      mutationFn: (v: { projectId: string; name: string; teamIds: string[] }) => apiPost('/api/tasks', v),
      onSuccess: (_d, v) => {
        done();
        invalidateTasks(v.projectId);
      },
    }),
    updateTask: useMutation({
      mutationFn: (v: { id: string; projectId: string; name: string; teamIds: string[] }) => apiPatch(`/api/tasks/${v.id}`, { name: v.name, teamIds: v.teamIds }),
      onSuccess: (_d, v) => {
        done();
        invalidateTasks(v.projectId);
      },
    }),
    archiveTask: useMutation({
      mutationFn: (v: { id: string; projectId: string }) => apiPost(`/api/tasks/${v.id}/archive`),
      onSuccess: (_d, v) => {
        done();
        invalidateTasks(v.projectId);
      },
    }),
    updateUser: useMutation({
      mutationFn: (v: { id: string; name: string; email: string }) =>
        apiPatch(`/api/users/${v.id}`, { name: v.name, email: v.email }),
      onSuccess: done,
    }),
    setRole: useMutation({ mutationFn: (v: { id: string; role: string }) => apiPatch(`/api/users/${v.id}/role`, { role: v.role }), onSuccess: done }),
    toggleDisabled: useMutation({ mutationFn: (id: string) => apiPost(`/api/users/${id}/toggle-disabled`), onSuccess: done }),
    deleteUser: useMutation({ mutationFn: (id: string) => apiDelete(`/api/users/${id}`), onSuccess: done }),
    invite: useMutation({
      mutationFn: (v: { email: string; role: string }) => apiPost('/api/invites', v),
      onSuccess: done,
    }),
    removeInvite: useMutation({
      mutationFn: (email: string) => apiDelete(`/api/invites/${encodeURIComponent(email)}`),
      onSuccess: done,
    }),
  };
}
